import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { City, Country, ICity, ICountry, IState, State } from "country-state-city";
import { findIndex } from "lodash";
import moment from "moment";
import { Subject, interval, lastValueFrom, of, timer } from "rxjs";
import { catchError, exhaustMap, finalize, map, switchMap, takeUntil, timeout } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { LoadingService } from "src/app/core/core-services/services/loader.service";
import { StripeService } from "src/app/core/core-services/services/stripe.service";
import { SubscriptionPreviewApiService } from "src/app/core/core-services/services/subscription-preview-api.service";
import { SubscriptionPlan, SubscriptionPlanType } from "src/app/core/models/stripe.models";
import { PreviewChangeWire } from "src/app/core/models/subscription-checkout.model";
import { selectPlanData } from "src/app/core/store/plan/plan.selectors";
import {
  normalizeIdentifierForCurrentPlanMatch,
  planTypeFromRouteParam,
  planTypeToIdentifier,
  planTypeToRouteKey,
} from "src/app/utils/subscription-plan.utils";
import { applyDiscount, couponDiscountAmount } from "src/app/utils/coupon-price.utils";
import { isNodeCouponRejection } from "src/app/utils/subscription-preview-error.utils";
import {
  CHECKOUT_3DS_REFUSED_MESSAGE,
  CHECKOUT_FINALISING_MESSAGE,
  CHECKOUT_GENERIC_ERROR_MESSAGE,
  classifyCheckoutError,
  isSafeUserMessage,
} from "src/app/utils/checkout-error.utils";
import { loadUserCurrentSubscriptionApi } from "src/app/core/store/plan/plan.actions";
import { formatPrice } from "src/app/utils/price-format.utils";
import { PlansLoadState, isPlanPriced, isValidPlansResponse } from "src/app/utils/plans-load-error.utils";
import { PLANS_LOAD_TIMEOUT_MS } from "../plans/plan-details.config";
import { CONTACT_SALES_TRACKING_EVENT, CONTACT_SALES_URL } from "src/app/utils/contact-sales.utils";
import { trackService } from "src/app/core/core-services/services/track.service";

import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { StoredDiscountService } from "src/app/core/core-services/services/stored-discount.service";
import { PendingCouponService } from "src/app/core/core-services/services/pending-coupon.service";
import { NotifyService } from "src/app/core/menu/not.service";
import { CookieService } from "ngx-cookie-service";
import { environment } from "src/environments/environment";
import { VngSelectOption } from "@vengreso/angular-components";

// Issue #609 — poll budgets. Exported so specs can shrink them rather than
// waiting out the real timeout.
export const THREE_D_SECURE_POLL_INTERVAL_MS = 2000;
export const THREE_D_SECURE_POLL_TIMEOUT_MS = 60000;
export const CONFIRMATION_PENDING_POLL_INTERVAL_MS = 3000;
export const CONFIRMATION_PENDING_POLL_TIMEOUT_MS = 90000;

export const stripeStyles = {
  base: {
    backgroundColor: "#F5F5F5",
    borderColor: "#EEEEEE",
    lineHeight: "3.829",
    padding: "30px 30px 30px 30px",
    iconColor: "#666EE8",
    fontWeight: 300,
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: "15px",
    "::placeholder": {
      color: "#CFD7E0",
    },
  },
};

@Component({
    selector: "app-payment-details",
    templateUrl: "./payment-details.component.html",
    styleUrls: ["./payment-details.component.scss"],
    standalone: false
})
export class PaymentDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("cardElement")
  cardElementContainer: ElementRef;

  @ViewChild("paymentElement")
  paymentElementContainer: ElementRef;

  @ViewChild("cardButton")
  cardButton: HTMLButtonElement;

  loading: boolean = false;
  paymentDetailsId: string = "paymentDetailsId";

  /**
   * Issue #608 F-5: the same `loading | ready | error` state
   * `NewPlanComponent` drives its own `v1/subscriptions/plans` load through
   * (issue #600 AC-7), applied here so the Contact sales CTA can only ever
   * describe a catalogue that actually finished loading. Defaults to
   * "loading" — `getPricing()`'s constructor-time call moves it to "ready"
   * or "error" once the request settles. The template gates the whole
   * order-review figure set (both the priced rows and `isContactSalesOnly`'s
   * Contact sales CTA) behind `plansLoadState === 'ready'`, shows the
   * existing skeleton while "loading", and shows the same #600/#602
   * "couldn't load our plans … Try again" block on "error" that /new-plan
   * and /pricing show. Pay stays unavailable in both non-ready states.
   */
  plansLoadState: PlansLoadState = "loading";

  // Issue #607: template-callable reference to the shared formatter, so
  // `payment-details.component.html`'s "Plan Type" line can render
  // `${{ formatPrice(planAmount) }}` instead of the previous raw
  // `${{planAmount}}` interpolation.
  formatPrice = formatPrice;

  // Field-initializer `inject()` rather than a constructor param, same
  // reasoning as `NewPlanComponent`'s identical field (issue #603): PR #601
  // rewrites this constructor, so a new param here is a guaranteed conflict.
  // `inject()` needs no constructor at all — see `contactSales()`/
  // `isContactSalesOnly` below. `trackService` is `providedIn: "root"`, so
  // unlike `NewPlanComponent` this needs no companion `NavigationService`
  // dependency for the URL half — `window.open()` directly is exactly what
  // `NavigationService.navigateToUrl()` does, with one fewer constructor
  // param for #601's rewrite to reconcile.
  private readonly trackService = inject(trackService);

  public client_secret = "";
  public planType: SubscriptionPlanType;
  public subType = SubscriptionPlanType;
  private _destroy$ = new Subject<void>();
  countries: ICountry[] = Country.getAllCountries();
  // R5: mat-select + mat-option -> vng-select's [options]="VngSelectOption[]" data
  // (03-component-mapping row 7) — populated in the constructor, after `countries`
  // is reordered to put United States first.
  countryOptions: VngSelectOption[] = [];
  country: string = "";
  countryStates: IState[] = State.getStatesOfCountry(this.country);
  state: string = "";
  stateCities: ICity[] = City.getCitiesOfState(this.country, this.state);
  city: string;
  zip;
  add1;
  add2;
  name;
  discountCode: any;
  checked;

  planTypeDisplay: string;

  discountCodeApplied: boolean;
  discountCodeInvalid: boolean;
  discountCodeNotSupplied: boolean;
  appliedDiscountCode: any;

  // --- Issue #609: checkout error handling ------------------------------
  //
  // "Still confirming" (flymsg-be #983's 403 `payment_confirmation_pending`)
  // is not an error — the charge is in flight and a resubmit is exactly
  // what that response asks the customer NOT to do (AC-3). `checkoutPending`
  // puts the page in a calm, non-error waiting state (an inline
  // `role="status"` notice, never the error toast) while a poll on
  // `v2/plans/current` waits for the charge to resolve on its own.
  checkoutPending = false;
  checkoutPendingMessage = "";
  // Set only once the AC-3 poll exhausts its budget without the plan
  // converging — only then does the Pay button re-enable, and only as an
  // explicit "Try again" (never a silent, immediate retry).
  pendingTimedOut = false;

  // Issue #610 F-1: the payment method this attempt is paying with,
  // captured once `confirmCardSetup()` returns it — `completeCardAuthentication()`
  // (3-D Secure) and `enterCheckoutPending()` (still-confirming) both need
  // it later, once their poll converges, to run `StripeService`'s success
  // side effects (`recordPurchaseSuccess()`) the exact same way the plain
  // 200 path does.
  private lastPaymentMethodId = "";

  // The plan's LIST price (issue #599: this is display-only — the "Plan
  // Type" row and the renewal line, §AC-7 — never the checkout amount).
  // Sourced from `v1/subscriptions/plans`, unchanged from pre-#599 builds.
  planAmount: any = 0;

  pricing: any = {};

  // --- Issue #599: the backend-previewed checkout amount ---------------
  //
  // Every amount a user can actually be charged (Discount / Total / Due
  // today / the Pay button) comes from flymsg-node's
  // `POST /api/v3/subscriptions/preview-change` — never a client-side
  // `list - coupon` figure (product decision Q2, "one path everywhere":
  // freemium, trial and paid plan changes all use this one preview call).
  // Stripe computes proration for a plan change and a plain subscribe
  // identically through the same endpoint, so the FE never has to know
  // which case it's in.
  preview: PreviewChangeWire | null = null;
  previewLoading = false;
  previewFailed = false;
  // The coupon code whose validated-and-applied result the CURRENT
  // `preview` reflects — `undefined` when the current preview has no
  // coupon. This, not `discount > 0`, is what confirmPaymentSetup() sends
  // as `discount_code` (AC-6): it can only ever be a code the preview
  // request itself carried and Stripe priced against, never a code the UI
  // merely displays as "applied".
  validatedPreviewCoupon: string | undefined;
  // The coupon the last successful preview request was made WITH (kept
  // even across a failed retry) so Retry re-asks with the right code.
  private lastPreviewCoupon: string | undefined;
  // Set when requestPreview() was held back because the plans catalogue was
  // not `ready` yet (or the plan is contact-sales-only) — see
  // requestPreview() and getPricing()'s `next` branch.
  private previewDeferred = false;
  // Every requestPreview() call pushes here instead of subscribing
  // directly. Piped through switchMap (constructor) so a newer request
  // ALWAYS supersedes an older one still in flight — unsubscribing cancels
  // the outstanding HTTP call and guarantees its late response can never
  // overwrite a newer figure (issue #601 S-23/G-02): apply HALF then TEN
  // before HALF's slow response lands, or clear the code while a coupon
  // preview is in flight, and only the LATEST action's result is ever
  // applied. This is also what keeps `validatedPreviewCoupon` — the code
  // `confirmPaymentSetup()` sends — always in lockstep with whatever is
  // currently displayed (AC-6): a superseded request's coupon can never
  // become the one sent to payments.
  private readonly previewRequests$ = new Subject<{ planIdentifier: string; coupon?: string }>();
  // The raw Stripe coupon object from the last successful
  // `v1/subscriptions/coupon` validation (unchanged, still Laravel-hosted —
  // only the DUE-TODAY amount moved to the node preview). Used solely for
  // the renewal line (AC-7: list price with the coupon applied only when
  // its `duration` isn't `"once"`) — never for the due-today figure.
  private lastValidatedCoupon: any = null;
  // "then $X/month (or /year) from <date>" (issue #599 product decision
  // Q1). `null` while unknown; the renewal row simply doesn't render then.
  renewalDate: moment.Moment | null = null;

  // Auto-apply support for a URL-supplied coupon (issue #592) and for a
  // code restored from an earlier visit (issue #594). A coupon percentage
  // is resolved against `planAmount`, which itself only becomes correct
  // once getPricing()'s `next` handler has run calculatePayment() —
  // applying a pending code before that would compute the discount against
  // a still-zero planAmount. `plansLoadState === "ready"` records whether
  // that has already happened by the time ngOnInit runs, and
  // `pendingUrlCouponCode` is the queued code when it hasn't (getPricing()
  // applies it once pricing actually arrives — see getPricing() below).
  // Despite the name, it now carries either source: a URL-supplied code
  // (issue #592) or a stored code being re-validated for the plan on screen
  // (issue #594) — ngOnInit picks which one, and only ever queues one of
  // them (a pending URL code always wins).
  //
  // Issue #608 F-7 (regression): this used to gate on a separate
  // "pricing settled" boolean that getPricing()'s `error` branch also set —
  // so a code queued from the URL (or restored from an earlier visit) got
  // "applied" against a still-zero planAmount the moment a first load
  // failed, showing "applied" with a 0.00 discount and sending no
  // `discount_code` at checkout. Gating on `plansLoadState === "ready"`
  // instead means a failed load always leaves the code queued;
  // retryPlansLoad() -> getPricing()'s `next` branch is the only path that
  // ever resolves it, against the real planAmount.
  private pendingUrlCouponCode: string | null = null;

  // The code applied on an earlier visit in this same browser tab (issue
  // #594), read from session-only storage at construction time. It is
  // never trusted at face value — no dollar amount is restored with it —
  // ngOnInit routes it through the same re-validation path a URL-supplied
  // code takes, against whatever plan is on screen right now, so a stale
  // amount or a code that no longer applies to this plan can never be
  // shown or sent (issue #594 decision 1).
  private storedDiscountCode: string | null = null;

  constructor(
    private dataService: DataService,
    private route: ActivatedRoute,
    private notification: NotificationService,
    private stripeService: StripeService,
    private _router: Router,
    private loadingService: LoadingService,
    public notifyService: NotifyService,
    private storedDiscount: StoredDiscountService,
    private cookieService: CookieService,
    private pendingCoupon: PendingCouponService,
    private previewApi: SubscriptionPreviewApiService,
    private store: Store
  ) {
    let USAIndex = findIndex(this.countries, { name: "United States" });
    [this.countries[0], this.countries[USAIndex]] = [this.countries[USAIndex], this.countries[0]];
    this.countryOptions = this.countries.map((item) => ({ value: item.isoCode, label: item.name }));

    this.getPricing();
    this.storedDiscountCode = this.storedDiscount.peek();

    // Single subscription for the component's lifetime (issue #601
    // S-23/G-02): switchMap guarantees only the LATEST requestPreview()
    // call's response is ever applied, no matter how many are in flight or
    // in what order their responses land.
    this.previewRequests$
      .pipe(
        switchMap(({ planIdentifier, coupon }) =>
          this.previewApi.previewChange(planIdentifier, coupon).pipe(
            map((response) => ({ coupon, result: response?.result ?? null, failed: false, isCouponRejection: false })),
            catchError((error) =>
              of({ coupon, result: null, failed: true, isCouponRejection: isNodeCouponRejection(error) })
            )
          )
        ),
        takeUntil(this._destroy$)
      )
      .subscribe(({ coupon, result, failed, isCouponRejection }) => {
        // Node rejecting THIS coupon (422, its OWN "not valid" message — see
        // isNodeCouponRejection's doc comment) is the same class of defect
        // as Laravel rejecting it (issue #601 S-26): show "not valid", not
        // the generic pricing-unavailable Retry loop. A 422 for a DIFFERENT
        // reason (plan unavailable, company-managed, …) must fall through
        // to the ordinary failure state instead (issue #601 round-2 QA
        // R-05) — a valid code must never be dropped just because the plan
        // itself couldn't be previewed. invalidateCoupon() itself re-asks
        // for the no-coupon preview through this same pipeline, so the
        // newer (coupon-less) request still wins over anything else in
        // flight.
        if (failed && coupon && isCouponRejection) {
          this.invalidateCoupon();
          return;
        }

        this.preview = result;
        this.previewFailed = failed || !result;
        this.previewLoading = false;
        this.validatedPreviewCoupon = result && coupon ? coupon : undefined;
      });
  }

  public elements = this.stripeService.stripe.elements();
  public paymentElement = this.elements.create("card", {
    style: stripeStyles,
    hidePostalCode: true,
  });

  ngOnInit(): void {
    this.planType = planTypeFromRouteParam(this.route.snapshot.paramMap.get("id"));
    if (
      this.planType === this.subType.YearlyStarter ||
      this.planType === this.subType.YearlyGrowth ||
      this.planType === this.subType.YearlySalesPro
    ) {
      this.checked = false;
    } else {
      this.checked = true;
    }
    this.computePlanTypeDisplay();

    // A direct link to /payment-details/:id?coupon=<code> honours the code
    // too (issue #592, "whole funnel" decision); captureFromParams also
    // carries forward a code stashed on an earlier funnel page when this
    // URL has no `coupon` query of its own.
    this.pendingCoupon.captureFromParams(this.route.snapshot.queryParamMap);
    const pendingCode = this.pendingCoupon.peek();
    let deferredCode: string | null = null;

    if (pendingCode) {
      // A pending URL-supplied code always wins over one restored from an
      // earlier visit (issue #592 decision 3, AC-6) — drop the stored code
      // outright rather than letting it resurface once the URL code is
      // consumed or cleared.
      this.storedDiscount.clear();
      this.storedDiscountCode = null;
      deferredCode = pendingCode;
    } else if (this.storedDiscountCode) {
      // A code restored from an earlier visit — for this plan or a
      // different one — is re-validated for the plan on screen right now,
      // through the same deferred path a URL-supplied code takes (issue
      // #594). Valid ⇒ apply() recomputes the amount fresh from the
      // response; invalid, expired, or refused for this plan ⇒ the normal
      // "not valid" failure path runs, which also drops the stored code
      // (see invalidateCoupon()).
      deferredCode = this.storedDiscountCode;
    }

    if (deferredCode) {
      // Issue #608 F-7 (merged into #601): gate on `plansLoadState ===
      // "ready"`, never a "settled" flag the `error` branch also sets — a
      // failed first load must leave the code queued so it is only ever
      // validated against a real, priced planAmount (getPricing()'s `next`
      // branch applies it after retryPlansLoad() succeeds).
      if (this.plansLoadState === "ready") {
        this.applyPendingUrlCoupon(deferredCode);
      } else {
        // Pricing hasn't loaded yet (the constructor's getPricing() call is
        // async in production), or it already failed — queue it either way;
        // getPricing()'s `next` branch applies it once planAmount is known
        // (needed for the renewal line's coupon math).
        this.pendingUrlCouponCode = deferredCode;
      }
    } else {
      // No coupon to wait for — ask for the base (no-coupon) preview right
      // away so Due today/Pay populate as fast as possible (issue #599).
      this.requestPreview();
    }

    this.requestRenewalDate();
  }

  ngAfterViewInit(): void {
    this.paymentElement.mount(this.paymentElementContainer.nativeElement);
  }

  ngOnDestroy(): void {
    // Deliberately does NOT clear the stored discount code here (unlike
    // pre-#594 builds): issue #594 decision 2 wants a manually applied code
    // to survive in-app navigation within the tab, not just a reload.
    // AppComponent clears it once navigation leaves the pricing/checkout
    // funnel entirely (StoredDiscountService.clearIfOutsideFunnel); a
    // successful purchase and an explicit empty-box Apply clear it here.
    this._destroy$.next();
    this._destroy$.complete();
  }

  updateStates(event) {
    this.countryStates = State.getStatesOfCountry(this.country);
  }

  updateCities(event) {
    this.stateCities = City.getCitiesOfState(this.country, this.state);
  }

  computePlanTypeDisplay(): void {
    let planTypepDisplay: string = "";

    switch (this.planType) {
      case this.subType.Starter:
        planTypepDisplay = "Starter (Monthly Plan)";
        break;

      case this.subType.YearlyStarter:
        planTypepDisplay = "Starter (Yearly Plan)";
        break;

      case this.subType.Growth:
        planTypepDisplay = "Growth (Monthly Plan)";
        break;

      case this.subType.YearlyGrowth:
        planTypepDisplay = "Growth (Yearly Plan)";
        break;

      // MonthlySalesPro ("sales-pro-monthly") is a separate route alias from
      // SalesPro ("sales-pro") for the SAME monthly plan (#424 F-1/S-18) —
      // both resolve to the same display and the same list price below.
      case this.subType.SalesPro:
      case this.subType.MonthlySalesPro:
        planTypepDisplay = "Business Pro (Monthly Plan)";
        break;

      case this.subType.YearlySalesPro:
        planTypepDisplay = "Business Pro (Yearly Plan)";
        break;

      default:
        break;
    }

    this.planTypeDisplay = planTypepDisplay;
  }

  /**
   * The plan's LIST price only (issue #599) — display for the "Plan Type"
   * row and the renewal line. Never used for Due today/Total/Pay; those
   * come exclusively from `preview` (requestPreview()).
   */
  calculatePayment(): void {
    let amount: any = 0;

    // Issue #607: assign the raw dollar number for both intervals — the
    // yearly branch previously pre-formatted through
    // `twoDecimal(displayedPrice(...))` while the monthly branch assigned
    // the raw `pricing[...]` float, so `planAmount` was a differently-typed
    // value per interval. The template now formats once, via
    // `formatPrice(planAmount)`.
    switch (this.planType) {
      case this.subType.Starter:
      case this.subType.YearlyStarter:
        amount = this.checked ? this.pricing["starter"] : this.pricing["starter-yearly"];
        break;

      case this.subType.Growth:
      case this.subType.YearlyGrowth:
        amount = this.checked ? this.pricing["growth"] : this.pricing["growth-yearly"];
        break;

      // MonthlySalesPro ("sales-pro-monthly") is a route alias for the same
      // monthly plan as SalesPro ("sales-pro") — missing this case rendered
      // "Plan Type $0/month" and, via renewalAmountDisplay below, "then
      // $0.00/month" too (issue #601 S-18/F-6).
      case this.subType.SalesPro:
      case this.subType.MonthlySalesPro:
      case this.subType.YearlySalesPro:
        amount = this.checked ? this.pricing["sales-pro-monthly"] : this.pricing["sales-pro-yearly"];
        break;

      default:
        break;
    }

    // Issue #608 F-1 (regression): a plan/interval `v1/subscriptions/plans`
    // omitted (contact-sales-only, same mechanism as #604's
    // `isContactSalesOnly` below) has no `pricing[...]` entry, so `amount`
    // is `undefined` here. Assigning that raw `undefined` used to flow
    // straight into `applyDiscount()` (`Math.max(0, undefined - 0)` ->
    // `NaN`) and into `formatPrice(undefined)` (`""`), rendering "Pay $NaN"
    // and a bare "$/year". Leaving the fields at their `0` default instead
    // is still not a price to show — the template hides every figure on
    // this card behind `isContactSalesOnly` regardless — but it keeps every
    // consumer of `planAmount` (`formatPrice(planAmount)`, the renewal
    // line's `applyDiscount()`) fed a finite number even if something ever
    // reads it outside that guard.
    if (amount !== undefined) {
      this.planAmount = amount;
    }
  }

  /**
   * True when the plan/interval this page is showing was omitted from
   * `v1/subscriptions/plans` — mirrors `NewPlanComponent.isContactSalesOnly`
   * (issue #603 AC-3) exactly, including the `isPlanPriced()` semantics
   * (checked with `!== undefined`, not truthiness, so a legitimately free
   * plan is never treated as contact-sales-only). `/payment-details` has no
   * billing-interval toggle of its own — `planType` is fixed by the route —
   * so this only needs to re-evaluate when pricing itself arrives.
   */
  get isContactSalesOnly(): boolean {
    return !isPlanPriced(this.pricing, planTypeToIdentifier(this.planType));
  }

  /**
   * Bound to the "Contact sales" CTA the template shows instead of the
   * order-review figures/Pay button when `isContactSalesOnly` is true
   * (issue #608 F-1) — the same URL/tracking event `NewPlanComponent`'s
   * "Contact sales" CTA already fires (issue #603 AC-1).
   */
  public contactSales(): void {
    this.trackService.btnClickUpdatetrack(CONTACT_SALES_TRACKING_EVENT);
    window.open(CONTACT_SALES_URL, "_blank");
  }

  toastMsg(msg: string) {
    this.notification.toast(msg, "OK", 2000);
  }
  public async confirmPaymentSetup() {
    // A "Try again" click (AC-3 pending-state timeout) re-enters here —
    // clear the pending notice so a fresh attempt starts clean.
    this.checkoutPending = false;
    this.pendingTimedOut = false;
    this.checkoutPendingMessage = "";

    if (!this.name) {
      this.toastMsg("Name on Card is Required");
      return;
    }
    if (!this.add1) {
      this.toastMsg("Address Line 1 is Required");
      return;
    }
    if (!this.city) {
      this.toastMsg("City is Required");
      return;
    }
    if (!this.state) {
      this.toastMsg("State is Required");
      return;
    }
    if (!this.zip) {
      this.toastMsg("Zip Code is Required");
      return;
    }
    if (!this.country) {
      this.toastMsg("Country is Required");
      return;
    }

    // AC-5: a `null`/failed preview must never become a silent fallback to
    // the list price. The button is already disabled while this is true
    // (template), but a submit racing an in-flight/failed preview must be
    // refused here too, not just visually.
    if (!this.preview || this.previewLoading) {
      this.toastMsg("We're still calculating your price — please wait a moment and try again.");
      return;
    }

    try {
      this.loading = true;
      this.loadingService.start(this.paymentDetailsId);

      this.client_secret = await lastValueFrom(this.stripeService.genPaymentIntent());

      const { setupIntent, error } = await this.stripeService.stripe.confirmCardSetup(this.client_secret, {
        payment_method: {
          card: this.paymentElement,
          billing_details: {
            name: this.name,
            address: {
              city: this.city,
              country: this.country,
              line1: this.add1,
              line2: this.add2,
              postal_code: this.zip,
              state: this.state,
            },
          },
        },
      });

      if (error) {
        const errorMessage =
          error.message ||
          "Please check your card details and try again. If this happens again, please contact heysupport@vengreso.com";
        this.notification.toast(errorMessage);
        this.loading = false;
        this.loadingService.stop(this.paymentDetailsId);
        return;
      }

      // What is SENT must come from the same source of truth as what is
      // DISPLAYED (issue #592 F-1, a regression against master; issue #599
      // carries the same discipline to the preview): `validatedPreviewCoupon`
      // is only ever set once a preview request that carried this exact
      // code has actually resolved (requestPreview()) — never from
      // `discountCodeApplied` alone, which onKeyUp() clears on any keyup
      // including a bare Tab landing focus in the field, and never from a
      // coupon the preview hasn't priced yet (AC-6).
      const validatedDiscountCode = this.validatedPreviewCoupon;
      this.lastPaymentMethodId = setupIntent.payment_method;

      this.stripeService
        .subToPlan(setupIntent.payment_method, this.planType, validatedDiscountCode)
        .pipe(finalize(() => this.loadingService.stop(this.paymentDetailsId)))
        .subscribe({
          next: () => this.onSubscribed(),
          error: (error) => this.handleCheckoutFailure(error),
        });
    } catch (error) {
      this.loading = false;
      this.loadingService.stop(this.paymentDetailsId);
      this.notification.toast(CHECKOUT_GENERIC_ERROR_MESSAGE);
    }
  }

  /** The shared "purchase went through" path — reached from a plain 200 (AC-nothing-new) and from the AC-2 3-D Secure poll resolving. */
  private onSubscribed(): void {
    this.clearForm();
    this.storedDiscount.clear();
    this.pendingCoupon.clear();
    this.checkoutPending = false;
    this.toastMsg("Thank you, for your subscription");
    this.cookieService.set("flymsg_planId", this.planType, {
      domain: environment.FLYMSG_DOMAIN,
      path: "/",
      secure: true,
      sameSite: "None",
    });
    this._router.navigate(["/settings"], {
      queryParams: { tab: "subscription" },
    });
  }

  /**
   * Issue #609 — the single place `POST v1/subscriptions/payments` failures
   * are turned into what the customer sees. `classifyCheckoutError` is
   * "known shapes only": anything it can't place becomes `unknown`, which
   * falls through to the same generic copy master always showed, never a
   * guess at what a backend message means.
   */
  private handleCheckoutFailure(err: unknown): void {
    this.loading = false;
    const failure = classifyCheckoutError(err);
    console.warn("[checkout] payment failed", {
      status: (err as { status?: number })?.status,
      kind: failure.kind,
    });

    switch (failure.kind) {
      case "server_error":
        // Request carries `ignoreErrors: true` (stripe.service.ts) so the
        // interceptor never ran DataService's own 5xx handling — replicate
        // it exactly by calling it directly (same pattern PR #606 used for
        // `startSalesProTrial()`), rather than re-deriving its copy/route.
        this.loadingService.stop(this.paymentDetailsId);
        this.dataService.handleError(err, { url: "v1/subscriptions/payments", isLoader: false });
        return;
      case "deactivated":
        // Same replication for `403 {code:"DEACTIVATED"}` — DataService's
        // sign-out dialog, never reimplemented here.
        this.loadingService.stop(this.paymentDetailsId);
        this.dataService.handleError(err, { url: "v1/subscriptions/payments", isLoader: false });
        return;
      case "requires_action":
        this.refreshPlanAfterIncompletePayment();
        void this.completeCardAuthentication(failure.clientSecret);
        return;
      case "confirmation_pending":
        this.enterCheckoutPending(failure.message);
        return;
      case "declined":
        this.refreshPlanAfterIncompletePayment();
        this.notification.toast(failure.message);
        return;
      case "refused":
        this.notification.toast(failure.message);
        if (failure.couponInvalid) {
          this.clearRefusedCoupon();
        }
        return;
      case "validation":
        this.notification.toast(failure.message);
        return;
      case "unknown":
      default:
        this.notification.toast(CHECKOUT_GENERIC_ERROR_MESSAGE);
        return;
    }
  }

  /** AC-6: a plan-change charge now has an unresolved state (`payment_pending`) that #598's banner reads. */
  private refreshPlanAfterIncompletePayment(): void {
    this.store.dispatch(loadUserCurrentSubscriptionApi({ refetchFromApi: true }));
  }

  /** AC-4 (invalid/reused coupon 403): drop the code so the customer can pay without it. */
  private clearRefusedCoupon(): void {
    this.validatedPreviewCoupon = undefined;
    this.discountCodeApplied = false;
    this.appliedDiscountCode = undefined;
    this.discountCodeInvalid = true;
    this.lastValidatedCoupon = null;
    this.storedDiscount.clear();
    this.pendingCoupon.clear();
    this.requestPreview();
  }

  /**
   * AC-2 — completes the 3-D Secure challenge Stripe.js opened in-page for
   * a 402 `requires_action` response. Never re-POSTs
   * `v1/subscriptions/payments`: the backend's PaymentIntent is what
   * settles the charge once Stripe confirms it.
   */
  private async completeCardAuthentication(clientSecret: string): Promise<void> {
    this.loading = true;
    try {
      const { paymentIntent, error } = await this.stripeService.stripe.confirmCardPayment(clientSecret);

      if (error) {
        // Product decision (issue #609): Stripe.js's own `card_error`
        // messages are user-facing decline text — show them. Every other
        // Stripe.js error type (a card_error is the one type meant to be
        // read by a customer) gets the fixed authorisation-failed copy.
        // issue #610 F-2: `card_error` text is still guarded against an
        // embedded Stripe id/URL (`isSafeUserMessage`) before it is shown —
        // "allowed" never meant "unguarded".
        const message =
          error.type === "card_error" && isSafeUserMessage(error.message) ? error.message : CHECKOUT_3DS_REFUSED_MESSAGE;
        this.loading = false;
        this.notification.toast(message);
        return;
      }

      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        const converged = await this.pollForPlanConvergence(
          (plan) => this.matchesPurchasedPlan(plan) && !plan.payment_pending,
          THREE_D_SECURE_POLL_INTERVAL_MS,
          THREE_D_SECURE_POLL_TIMEOUT_MS
        );
        this.loading = false;
        if (converged) {
          // issue #610 F-1: run the exact same success side effects the
          // plain-200 path runs (plan-store refresh, analytics, extension
          // notify) BEFORE navigating, so Settings already shows the
          // purchased plan with no reload.
          //
          // issue #610 F-8 (round 2): pass `planTypeToIdentifier(this.planType)`,
          // NOT `converged.identifier`. `getCurrentPlan()` (`v2/plans/current`)
          // rewrites `"sales-pro-monthly"` to `"sales-pro"` on the object it
          // returns (`normalizeIdentifierForCurrentPlanMatch`'s doc comment) —
          // that's fine for matching convergence, but `converged.identifier`
          // is that already-normalised value, so the `subscription_purchased_FlyMSG`
          // event and `sharedData.planIdType` reported `"sales-pro"` for a 3DS
          // Monthly Sales Pro purchase while the plain-200 path (which sends
          // the un-normalised `result.data.identifier` echoed back from the
          // subscribe call) reported `"sales-pro-monthly"` — same plan, two
          // values in analytics. `planTypeToIdentifier(this.planType)` is the
          // same un-normalised identifier the plain-200 path's request (and
          // therefore its echoed response) used, so every path now reports
          // identically for every plan, not just the ones normalisation
          // happens to leave untouched (Yearly, Starter).
          this.stripeService.recordPurchaseSuccess(
            this.planType,
            planTypeToIdentifier(this.planType),
            this.lastPaymentMethodId
          );
          this.onSubscribed();
        } else {
          this.notification.toast(CHECKOUT_FINALISING_MESSAGE);
          this._router.navigate(["/settings"], { queryParams: { tab: "subscription" } });
        }
        return;
      }

      // Any other paymentIntent.status — stay on the page (AC-1 behaviour).
      this.loading = false;
      this.notification.toast(CHECKOUT_3DS_REFUSED_MESSAGE);
    } catch {
      // issue #610 F-4: `confirmCardPayment` rejecting/throwing (a network
      // or integration failure, not a Stripe.js `{error}` resolution) must
      // still end loading, re-enable Pay and tell the customer something —
      // never strand the page with a disabled button and no message.
      this.loading = false;
      this.notification.toast(CHECKOUT_3DS_REFUSED_MESSAGE);
    } finally {
      this.loadingService.stop(this.paymentDetailsId);
    }
  }

  /** AC-3 — the still-confirming 403 puts the page in a calm, non-error waiting state instead of the error toast. */
  private enterCheckoutPending(message: string): void {
    this.loading = false;
    this.loadingService.stop(this.paymentDetailsId);
    this.checkoutPending = true;
    this.pendingTimedOut = false;
    this.checkoutPendingMessage = message;

    this.pollForPlanConvergence(
      (plan) => this.matchesPurchasedPlan(plan) && !plan.on_trial,
      CONFIRMATION_PENDING_POLL_INTERVAL_MS,
      CONFIRMATION_PENDING_POLL_TIMEOUT_MS
    ).then((converged) => {
      if (converged) {
        // issue #610 F-1: same success side effects the plain-200 path
        // runs, before onSubscribed() navigates away — otherwise Settings
        // shows the pre-purchase plan (e.g. still "14-day Trial Active")
        // until a reload.
        //
        // issue #610 F-8 (round 2): `planTypeToIdentifier(this.planType)`,
        // not `converged.identifier` — see `completeCardAuthentication()`'s
        // matching comment. Same mismatch, same fix, on the still-confirming
        // path.
        this.stripeService.recordPurchaseSuccess(
          this.planType,
          planTypeToIdentifier(this.planType),
          this.lastPaymentMethodId
        );
        this.onSubscribed();
        return;
      }
      // Timeout: the notice stays, and the Pay button re-enables ONLY now,
      // labelled "Try again" — never a blind immediate retry (AC-3).
      this.pendingTimedOut = true;
    });
  }

  private matchesPurchasedPlan(plan: SubscriptionPlan | null | undefined): boolean {
    if (!plan) return false;
    return (
      normalizeIdentifierForCurrentPlanMatch(plan.identifier) ===
      normalizeIdentifierForCurrentPlanMatch(planTypeToIdentifier(this.planType))
    );
  }

  /**
   * Polls `StripeService.getCurrentPlan()` until `predicate` is true or
   * `timeoutMs` elapses, whichever comes first. Resolves with the
   * converged plan (so the caller can read its resolved `identifier` for
   * `recordPurchaseSuccess()`, issue #610 F-1) or `null` on timeout/no
   * convergence. Stops on component destroy (`takeUntil(this._destroy$)`)
   * so a poll never outlives the page — a transient failure to fetch the
   * plan is treated as "not yet converged" rather than aborting the poll.
   *
   * issue #610 F-3: the deadline used to be checked only inside the poll's
   * own `next` callback, using `interval(...).pipe(switchMap(...))`. When
   * `getCurrentPlan()` takes longer than `intervalMs` to answer, `switchMap`
   * cancels each in-flight request as the next tick starts it over — so
   * `next` never fires and the timeout is never evaluated: the poll runs
   * forever. `timer(timeoutMs)` below is exactly that fix — it fires once,
   * on its own clock, and forces resolution regardless of the poll's state.
   *
   * issue #610 F-7 (round 2): `switchMap` was still wrong on its own terms.
   * QA proved that once a single `v2/plans/current` answer is slower than
   * `intervalMs` (a 4 s pending answer against the 3 s interval; a 2.5 s
   * 3DS answer against the 2 s interval), EVERY tick aborts the request the
   * previous tick started before it can ever resolve — `next` never fires
   * with a value, so the poll can never see a convergence the backend
   * already committed, and (on the 3DS path) `recordPurchaseSuccess()`
   * never runs even though `Settings` itself shows the right plan. The
   * client-side abort doesn't even stop the backend request, so requests
   * pile up server-side while never overlapping on the client (F-7's
   * "Server side" note). `exhaustMap` is the fix: it lets the in-flight
   * request finish — however long that takes — and ignores ticks that land
   * while one is outstanding, so requests are still serialized (never two
   * in flight at once, keeping F-3's guarantee) but a slow answer is
   * waited for instead of cancelled. The independent `timer(timeoutMs)`
   * deadline still holds regardless: it settles the poll on its own clock
   * even if `exhaustMap`'s current request never resolves at all.
   */
  private pollForPlanConvergence(
    predicate: (plan: SubscriptionPlan) => boolean,
    intervalMs: number,
    timeoutMs: number
  ): Promise<SubscriptionPlan | null> {
    return new Promise<SubscriptionPlan | null>((resolve) => {
      let settled = false;
      const settle = (value: SubscriptionPlan | null) => {
        if (settled) return;
        settled = true;
        pollSubscription.unsubscribe();
        deadlineSubscription.unsubscribe();
        resolve(value);
      };

      const deadlineSubscription = timer(timeoutMs)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => settle(null));

      const pollSubscription = interval(intervalMs)
        .pipe(
          exhaustMap(() => this.stripeService.getCurrentPlan().pipe(catchError(() => of(null)))),
          takeUntil(this._destroy$)
        )
        .subscribe({
          next: (plan) => {
            if (plan && predicate(plan)) {
              settle(plan);
            }
          },
          complete: () => settle(null),
        });
    });
  }

  public backClicked() {
    // An unresolvable plan (garbage :id param) must still navigate somewhere —
    // ["/new-plan", undefined] throws NG04008 and leaves the back link dead.
    if (!this.planType) {
      this._router.navigate(["pricing/"]);
      return;
    }

    // The route must carry the enum KEY: passing the value ("sales-pro" for
    // monthly SalesPro) lands on /new-plan/sales-pro, which rendered blank.
    this._router.navigate(["/new-plan", planTypeToRouteKey(this.planType)]);
  }

  clearForm() {
    this.name = "";
    this.add1 = "";
    this.city = "";
    this.state = "";
    this.zip = "";
    this.country = null;
    this.discountCode = "";
  }

  /**
   * `isAutoApply` is true only when applyPendingUrlCoupon() calls this on
   * the user's behalf for a URL-supplied code. A 5xx from the validation
   * endpoint must not eject an arriving user to the global "Something went
   * wrong" page for a page load they didn't ask to validate anything on —
   * it must fall back to the normal price with the same clear message an
   * invalid code gets (AC-3). The manual "Apply" button (isAutoApply left
   * false) keeps DataService's centralized 5xx handling exactly as master
   * has it (issue #592 F-4; that handling is itself pre-existing, tracked
   * separately) — only the request this call makes is marked
   * `ignoreErrors`, and only for the auto-apply path; the error still
   * reaches the `error` callback below either way, which already treats
   * every failure the same as an invalid code.
   *
   * `discountCodeApplied`/`appliedDiscountCode` below are written
   * optimistically, before validation answers, and the stale-answer guard
   * a few lines down returns without ever unwinding them (issue #601
   * round-5 QA L-1r) — that is BY DESIGN for `confirmPaymentSetup()`'s
   * send-gate (see its comment), which reads `validatedPreviewCoupon`
   * instead, never these two fields. The "was applied" label and the
   * Discount row's parenthetical (round-7 QA L-3) don't read these two
   * fields either, for the same reason: both read `validatedPreviewCoupon`
   * (`discountCodeLabel` for the label; the row's `*ngIf`/interpolation
   * directly), a source set only once a preview has actually confirmed a
   * code, so neither can ever lag behind or contradict what this method
   * optimistically writes here.
   */
  apply(isAutoApply: boolean = false) {
    this.discountCodeInvalid = this.discountCodeNotSupplied = this.discountCodeApplied = false;
    this.appliedDiscountCode = this.discountCode;

    if (!this.discountCode?.trim()) {
      this.discountCodeNotSupplied = true;
      // Clicking Apply on an empty box is an explicit clear — drop anything
      // persisted for a later visit too (issue #594 AC-8 parity), not just
      // the in-memory/display state reset above, and re-preview without a
      // coupon (issue #599 AC-3: clearing a code re-requests the preview).
      this.storedDiscount.clear();
      this.lastValidatedCoupon = null;
      this.requestPreview();
      return;
    }

    this.discountCodeApplied = true;

    // Scanning the identifier enum for a key or value matching the plan type matched
    // nothing for monthly Sales Pro ("sales-pro" is neither), so the "freemium"
    // initialiser went out instead — validating the coupon against the wrong, and
    // very real, plan (#424).
    const planIdentifier = planTypeToIdentifier(this.planType);

    if (!planIdentifier) {
      this.invalidateCoupon();
      return;
    }

    // Captured now, not re-read once this async validation resolves (issue
    // #601 round-2 QA R-04): the user is free to type a DIFFERENT code
    // into the field before Laravel answers, and the guard just below
    // needs to know what code THIS answer actually belongs to, regardless
    // of what the field holds by the time it arrives.
    const validatedCode = this.discountCode;

    this.stripeService.retrieveCouponObject(validatedCode, planIdentifier, isAutoApply).subscribe({
      next: (response: any) => {
        // Issue #601 round-3 QA R-04/C-03/F-11 (the round-2 fix's mirror
        // case, C-03b): the field may already hold a DIFFERENT code by the
        // time this answer lands — typed over without a fresh Apply click.
        // Round 2 only guarded `lastValidatedCoupon` against a mismatched
        // coupon, but `applyCoupon()` still ran and re-previewed whatever
        // the field held THEN, so Due today/Pay could end up pricing a
        // code (the one now in the field) that was never itself validated
        // or Apply-clicked, while the renewal line — correctly left alone
        // — kept describing the OLD state. Ignoring a stale answer
        // entirely, before it can touch anything, keeps every figure
        // (Due today, Pay, the renewal line) describing the same code: the
        // one this answer was actually FOR, or nothing changes at all.
        if (validatedCode !== this.discountCode) {
          return;
        }

        const coupon = response?.result;

        if (coupon?.valid !== true) {
          this.invalidateCoupon();
          return;
        }

        this.applyCoupon(coupon, validatedCode);
      },
      error: () => {
        // Same guard as `next` above, for the failure path (round-3
        // QA R-04/C-03e): a stale code's rejection must not invalidate
        // whatever different code the user has since typed.
        if (validatedCode !== this.discountCode) {
          return;
        }
        this.invalidateCoupon();
      },
    });
  }

  applyCoupon(coupon: any, code: string = this.discountCode): void {
    // Only the CODE is persisted (issue #594 decision 1) — never the dollar
    // amount. A later visit re-validates the code and re-previews fresh
    // against whatever plan is on screen then; the figure computed here,
    // for THIS plan, is never carried over.
    this.storedDiscount.set(this.discountCode);
    this.appliedDiscountCode = this.discountCode;
    this.discountCodeApplied = true;
    // `apply()` above (round-3 QA R-04/C-03/F-11) now ignores a stale
    // validation answer entirely before it ever reaches this method, so
    // `code` — the code THIS coupon object was actually validated for —
    // and `this.discountCode` are guaranteed equal here; the check is kept
    // as defense-in-depth for `applyCoupon()`'s other caller (a direct,
    // synchronous call, e.g. from a test) rather than relied on as the fix.
    if (code === this.discountCode) {
      this.lastValidatedCoupon = coupon;
    }
    this.requestPreview(this.discountCode);
  }

  invalidateCoupon(): void {
    this.discountCodeInvalid = true;
    // Whatever code just failed validation — typed fresh or restored from
    // an earlier visit — must not be offered again on the next visit
    // (issue #594 decision 1: "if it is not valid, drop it").
    this.storedDiscount.clear();
    this.lastValidatedCoupon = null;
    // Revert Due today/Total/Pay to the no-coupon preview (issue #599
    // AC-3): whatever preview a previously-valid code produced must not
    // keep being shown once that code (or its replacement) turns out
    // invalid.
    this.requestPreview();
  }

  onKeyUp(event) {
    setTimeout(() => {
      this.discountCodeApplied = false;
    }, 500);
  }

  // Issue #607: delegate to the shared formatter (see the identical note on
  // `NewPlanComponent`, which carried the same truncating copy).
  displayedPrice(amount: any): string {
    return formatPrice(amount);
  }

  twoDecimal(amount: any): string {
    return formatPrice(amount);
  }

  /**
   * See `NewPlanComponent.loadPlans()` — issue #608 F-6 ports the identical
   * `v1/subscriptions/plans` load contract here: `ignoreErrors: true` (a
   * 5xx must land in this page's own "error" state, never DataService's
   * centralized 5xx handling that hard-navigates to `/session/error`),
   * `timeout(PLANS_LOAD_TIMEOUT_MS)` (a hung request that never answers
   * must still resolve to "error" instead of leaving the loading skeleton
   * up forever), and `isValidPlansResponse()` (a 200 whose body isn't a
   * price list this page can destructure must also resolve to "error"
   * rather than throwing synchronously inside `next`, which stranded
   * `plansLoadState` on "loading" forever — RxJS never routes an error
   * thrown from `next` to this same subscription's `error` handler).
   * `takeUntil(this._destroy$)` mirrors `NewPlanComponent` too, so a
   * response that arrives after the component is torn down never touches
   * `pricing`/`plansLoadState`.
   */
  getPricing() {
    this.plansLoadState = "loading";
    this.dataService
      .get({ url: "v1/subscriptions/plans", isLoader: false, ignoreErrors: true })
      .pipe(timeout(PLANS_LOAD_TIMEOUT_MS), takeUntil(this._destroy$))
      .subscribe({
        next: (response: any) => {
          if (!isValidPlansResponse(response)) {
            // Issue #608 F-6: a malformed 200 must land here, not throw out
            // of `next` — see `NewPlanComponent.loadPlans()`'s identical
            // guard (QA #602 F-2 r2). planAmount stays at its 0 default, so
            // — same as the network-error branch below — a queued coupon
            // must stay queued (F-7), never be applied against it.
            this.plansLoadState = "error";
            return;
          }
          response.result.forEach(({ identifier, unit_amount }) => {
            this.pricing[identifier] = unit_amount / 100;
          });

          this.calculatePayment();
          // Issue #608 F-5: only once a catalogue has actually loaded does
          // `isContactSalesOnly` get to decide whether this plan is priced —
          // see the template, which reads `plansLoadState` before it ever
          // reads that getter.
          this.plansLoadState = "ready";
          // Issue #608 F-7: this is the ONLY place a queued coupon is ever
          // applied — planAmount is finally a real, priced number here, so
          // couponDiscountAmount() computes a real discount instead of $0.
          if (this.pendingUrlCouponCode) {
            this.applyPendingUrlCoupon(this.pendingUrlCouponCode);
          } else if (this.previewDeferred) {
            // #608 must-keep (g), merged into #601: a preview asked for
            // before the catalogue was ready (ngOnInit's no-coupon preview,
            // in production always) was held back by requestPreview() —
            // ask for it now. A contact-sales-only plan is held back again.
            this.requestPreview(this.lastPreviewCoupon);
          }
        },
        error: () => {
          // Issue #608 F-7 (regression): this branch used to call
          // applyPendingUrlCoupon() right here, while planAmount was still
          // its 0 default — a queued percent-off code came back "applied"
          // with a 0.00 discount, and the checkout that followed sent no
          // `discount_code` at all (a fixed-amount code isn't affected by
          // planAmount, but got the same premature-apply treatment). The
          // code must stay queued in `pendingUrlCouponCode` instead:
          // retryPlansLoad() -> getPricing()'s `next` branch above is the
          // only path that ever resolves it, once pricing is real. This
          // covers a URL `?coupon=` code and one restored from an earlier
          // visit (issue #594) identically, since both are queued through
          // the same `pendingUrlCouponCode` field.
          //
          // Issue #608 F-5: a failed load used to leave `isContactSalesOnly`
          // evaluating against the still-empty `pricing` object,
          // permanently telling a self-serve subscriber their plan was
          // sales-only, with no retry. It now shows the same in-page
          // "couldn't load our plans … Try again" block #600/#602 show on
          // /new-plan and /pricing.
          this.plansLoadState = "error";
        },
      });
  }

  /**
   * Bound to the in-page error state's "Try again" button (issue #608 F-5,
   * mirrors `NewPlanComponent.retryPlansLoad()` — issue #600 AC-2/AC-7).
   */
  retryPlansLoad(): void {
    this.getPricing();
  }

  /** Pre-fills the discount field with a code from either deferred source —
   * a URL-supplied code (issue #592) or one restored from an earlier visit
   * (issue #594) — and runs the same validation the manual Apply button
   * triggers (issue #592 — auto-apply decision). Uses `apply()` itself
   * rather than a parallel code path, so the request this produces is
   * identical to the manual flow (the parity oracle in the plan) and any
   * future change to apply()'s validation logic can't drift between entry
   * points; in particular a stored code is never applied at its old
   * amount — apply()/applyCoupon() always recompute it fresh.
   */
  private applyPendingUrlCoupon(code: string): void {
    this.pendingUrlCouponCode = null;
    this.discountCode = code;
    this.apply(true);
  }

  // --- Issue #599: preview + renewal-line helpers -----------------------

  /**
   * Requests (or re-requests) the backend preview for the plan on screen,
   * optionally with a coupon code — the single source of every amount a
   * paying user can be charged (Plan/Discount/Total/Due today/Pay). Called
   * on initial load, after every successful coupon validation, and after
   * every clear/invalidate, so the displayed figure and the code that
   * produced it (`validatedPreviewCoupon`) are always in lockstep (AC-3,
   * AC-6).
   */
  requestPreview(coupon?: string): void {
    const planIdentifier = planTypeToIdentifier(this.planType);
    if (!planIdentifier) {
      // No resolvable plan (a garbage :id param) — nothing to preview; the
      // page has no plan to display or sell in the first place.
      this.previewLoading = false;
      this.previewFailed = true;
      this.preview = null;
      this.validatedPreviewCoupon = undefined;
      return;
    }

    // #608 must-keep (g), merged into #601: no preview-change request while
    // the plans catalogue is not `ready` (its figures are not on screen —
    // the template shows the skeleton or the "Try again" block instead), and
    // never for a contact-sales-only plan (the catalogue omitted it, so there
    // is nothing for Stripe to price). The request is remembered and
    // getPricing()'s `next` branch re-issues it once the catalogue is ready.
    if (this.plansLoadState !== "ready" || this.isContactSalesOnly) {
      this.lastPreviewCoupon = coupon;
      this.previewDeferred = true;
      this.previewLoading = false;
      this.previewFailed = false;
      this.preview = null;
      this.validatedPreviewCoupon = undefined;
      return;
    }
    this.previewDeferred = false;

    this.lastPreviewCoupon = coupon;
    this.previewLoading = true;
    this.previewFailed = false;
    // Hide the outgoing preview's figures immediately, not just once the
    // new response lands (issue #601 G-02): otherwise the old Due
    // today/Total stay visible under the NEW code's label while
    // "Calculating…" replaces only the Pay button.
    this.preview = null;

    this.previewRequests$.next({ planIdentifier, coupon });
  }

  /** Re-asks for the preview this page last tried for (AC-5's Retry action). */
  retryPreview(): void {
    this.requestPreview(this.lastPreviewCoupon);
  }

  /**
   * "then $X/month (or /year) from <date>" (issue #599 product decision
   * Q1). The amount is the plan's LIST price (never the previewed
   * due-today figure) with the last validated coupon applied only when its
   * `duration` isn't `"once"` (AC-7) — a once-only coupon never reduces the
   * renewal charge.
   */
  get renewalAmountDisplay(): string {
    const listPrice = Number(this.planAmount) || 0;
    if (this.lastValidatedCoupon && this.lastValidatedCoupon.duration !== "once") {
      return applyDiscount(listPrice, couponDiscountAmount(this.lastValidatedCoupon, listPrice));
    }
    return listPrice.toFixed(2);
  }

  get discountDisplay(): string {
    return this.centsToDollars(this.preview?.discount_amount);
  }

  /**
   * The code the "Discount code X was applied" label (Order review, above
   * the Discount/Total/Due today rows) may show — issue #601 round-5 QA
   * L-1r. `null` whenever the field does not hold EXACTLY the code the
   * CURRENT preview was actually validated and priced with
   * (`validatedPreviewCoupon`, the same source `confirmPaymentSetup()`
   * sends), so the label never claims a code that is not applied for:
   *
   *  - a stale validation answer `apply()`'s guard dropped because the
   *    field moved on before Laravel answered (the field can move on by a
   *    keystroke, a paste, an autofill, or a direct `discountCode` write —
   *    this is a plain comparison re-evaluated on every change-detection
   *    pass, not an event listener, so it catches all of them alike,
   *    unlike the old `onKeyUp`-only hide);
   *  - a validation still in flight (nothing confirmed yet — closes the
   *    ~0.5–5 s window where `apply()` used to claim the code "was
   *    applied" before Laravel had even answered);
   *  - the field holding a different-case or otherwise different string
   *    than the code that was actually priced.
   *
   * Deliberately NOT `appliedDiscountCode`: `apply()` writes that
   * optimistically before validation answers, and the stale-answer guard
   * returns without ever unwinding it — that combination is L-1r's root
   * cause (see `apply()`'s doc comment).
   */
  get discountCodeLabel(): string | null {
    return this.validatedPreviewCoupon && this.validatedPreviewCoupon === this.discountCode
      ? this.validatedPreviewCoupon
      : null;
  }

  /**
   * The Discount row must not render at all when the preview's
   * `discount_amount` is 0 (issue #601 round-2 QA G-03/R-08) — Stripe folds
   * a coupon's effect into the proration line items on a same-interval
   * change instead of a standalone discount total, so a real, applied
   * coupon can still leave `discount_amount` at 0 even though Pay is
   * genuinely lower. Showing "Discount (CODE) 0.00" in that case reads as
   * the code having no effect, which is false — Total/Due today already
   * carry the real (discounted) figure either way.
   */
  get discountVisible(): boolean {
    return (this.preview?.discount_amount ?? 0) > 0;
  }

  /**
   * The preview's one-time prorated invoice total, signed (issue #601
   * G-03) — "$15.00" for a charge, "-$109.67" for a downgrade/yearly-
   * >monthly credit that lands on the customer balance instead of being
   * charged. Never carries a "/month" or "/year" suffix: unlike the Plan
   * Type row above, this is not a recurring rate.
   */
  get totalDisplay(): string {
    const cents = this.preview?.total ?? 0;
    const dollars = (Math.abs(cents) / 100).toFixed(2);
    return cents < 0 ? `-$${dollars}` : `$${dollars}`;
  }

  /**
   * True when the preview's `total` is negative — a downgrade/yearly-
   * >monthly credit that is applied to the customer's Stripe balance, never
   * refunded to the card (issue #601 round-2 QA G-03/R-08). The template
   * shows a clarifying note next to `totalDisplay`'s signed "-$X.XX" only
   * in this case, so it's never misread as a refund.
   */
  get totalIsCredit(): boolean {
    return (this.preview?.total ?? 0) < 0;
  }

  get dueTodayDisplay(): string {
    return this.centsToDollars(this.preview?.due_today);
  }

  private centsToDollars(cents: number | undefined): string {
    return ((cents ?? 0) / 100).toFixed(2);
  }

  /**
   * Renewal date for the line above (issue #599 Q1; issue #601 round-1 and
   * round-2 QA S-17/R-06).
   *
   * Round 1 asked `GET v1/subscriptions/next-billing` (the CURRENT period's
   * days-remaining) for every case, which is wrong for a trial conversion, a
   * monthly<->yearly interval switch, and freemium. The round-1 fix
   * over-corrected to "today + one interval" for every case instead, which
   * QA proved wrong against real, MID-PERIOD Stripe subscriptions: Stripe
   * keeps the existing billing anchor for a plain same-interval plan swap
   * and only restarts the period on a brand-new subscription, a trial
   * conversion, or an interval change. The two rules apply to different
   * cases:
   *
   *  - brand-new subscribe (freemium today), a trial converting to paid, or
   *    a monthly<->yearly interval switch: Stripe restarts the period as of
   *    today, in the TARGET plan's cadence — "today + one interval".
   *  - a same-interval plan swap on an EXISTING, non-trialing paid
   *    subscription (S-01/S-02/S-07/S-08/S-16b's class): Stripe keeps the
   *    CURRENT subscription's billing anchor untouched — the date it was
   *    already going to renew on, regardless of the swap.
   *
   * `PreviewChangeWire` (flymsg-node issue #17/#57, verified against
   * `origin/issue-253-preview-trial-no-pm`) still carries no period-end
   * field — if a future contract adds one, prefer it here instead of this
   * two-branch computation. Which branch applies is read off the current
   * subscription data the app already loads app-wide at bootstrap/login
   * (`plan.effects.ts`'s `loadUserCurrentSubscriptionApi`, `selectPlanData`)
   * rather than a new call — the same store `PlansDetailsComponent` already
   * re-derives from on every emission (issue #592 F-2's "Store.select
   * replays the current value on subscribe" pattern, mirrored here): a
   * `null` first emission (not loaded yet) computes the brand-new-
   * subscribe answer as an interim value, corrected the moment the real
   * plan arrives. Only the "keep the anchor" branch below needs a network
   * round trip, for the anchor DATE itself (`v1/subscriptions/next-billing`).
   *
   * Round 3 (issue #601 QA S-17/R-06/T-01/T-02/T-04/T-09): `next-billing`'s
   * `days` is `DateInterval::$days`, a FLOORED whole-day count — deriving a
   * calendar date from it (`moment().add(days, "days")`) is one day early
   * whenever the fractional day crosses local midnight, which is every time
   * the anchor's time-of-day is earlier than now's, and always for a
   * same-day subscription. flymsg-be #985 (`origin/issue-984-next-billing-
   * timestamp`) adds the exact anchor instant instead: on the keep-the-
   * anchor branch this now reads `current_period_end` (ISO-8601 Z) directly
   * — `moment(...)` parses it and renders it in the browser's local zone —
   * rather than re-deriving a day count. The old integer `days` shape (a
   * stale FE against a not-yet-deployed BE) and `{"result": false}` (no
   * `main` subscription) both degrade to hiding the line: guessing at a
   * date from a shape this method doesn't understand is worse than no line.
   *
   * `ignoreErrors: true` (round-3 QA N-02/F-10): `next-billing` reads
   * Stripe live on every call, so a Stripe hiccup answers a 503 (or the
   * request can fail outright). DataService's global handler hard-navigates
   * >=500 responses to `/session/error` by default — passing `ignoreErrors`
   * opts this call out of that, so a 503/5xx never ejects a user who is
   * otherwise holding a valid, already-computed price. Both the 503 and a
   * plain network error land in the same `error` callback below and hide
   * the line rather than fabricate one — round 2's `today + intervalDays()`
   * fallback showed a confident but wrong date on a network error; this
   * round removes that fabrication entirely.
   *
   * Round 4 (issue #601 round-4 QA N-02/N-03/D-03): `selectPlanData`'s
   * `null` first emission (store not loaded yet — a cold load or reload,
   * e.g. an offer link straight to this page) was ALSO being read as "no
   * existing Stripe subscription", so it took the new-subscribe branch and
   * set `renewalDate = today + 1 interval` as an "interim" value. On a
   * cold load flymsg-be's `v2/plans/current` can take seconds; on a hang it
   * never resolves. That fabricated date sat on screen the whole time —
   * exactly what this method exists to prevent. A `null` plan is genuinely
   * ambiguous (not-loaded-yet vs. a real answer) in a way "freemium"
   * (identifier `"freemium"`/`"free"`) is not, so it no longer drives any
   * branch: `null` now only ever clears the line and waits for the real
   * plan. The same-interval branch also clears the line BEFORE firing the
   * `next-billing` round trip (rather than leaving whatever was on screen
   * from the previous emission/plan), so nothing is ever shown while that
   * call is pending, and nothing stays if it never answers.
   */
  private requestRenewalDate(): void {
    this.store
      .select(selectPlanData)
      .pipe(takeUntil(this._destroy$))
      .subscribe((currentPlan) => this.applyRenewalDateRule(currentPlan));
  }

  private applyRenewalDateRule(currentPlan: SubscriptionPlan | null): void {
    if (!currentPlan) {
      // Store not loaded yet (cold load/reload) — this is NOT "no
      // subscription", it is "we don't know yet". Hide the line rather
      // than guess; the real rule below re-runs once the plan resolves
      // (issue #601 round-4 QA N-02/N-03/D-03).
      this.renewalDate = null;
      return;
    }

    const targetInterval = this.checked ? "month" : "year";
    const identifier = currentPlan.identifier?.toLowerCase();
    // The unpriced Freemium tier: there is no existing Stripe
    // subscription/anchor to keep — this is a brand-new subscribe.
    const isNewSubscription = identifier === "freemium" || identifier === "free";
    const isTrialConversion = !!currentPlan.on_trial;
    const isIntervalChange = !isNewSubscription && currentPlan.interval !== targetInterval;

    if (isNewSubscription || isTrialConversion || isIntervalChange) {
      this.renewalDate = moment().add(1, this.checked ? "month" : "year");
      return;
    }

    // Same-interval change on an existing, non-trialing paid subscription:
    // Stripe KEEPS the current billing anchor (issue #601 round-2 QA
    // S-17/R-06) — verified against real Stripe test-mode subscriptions
    // mid-period, created on a test clock or back-dated (a same-day
    // subscription's anchor coincides with "today + one interval" either
    // way, which is why round 1's real-Stripe checks didn't catch this).
    //
    // Clear any provisional/stale date BEFORE the round trip (round-4 QA
    // N-02/N-03/D-03): nothing is shown while this call is pending, and
    // nothing stays on screen if it never answers.
    this.renewalDate = null;
    this.dataService
      .get({ url: "v1/subscriptions/next-billing", isLoader: false, ignoreErrors: true })
      .subscribe({
        next: (response: any) => {
          const result = response?.result;
          const periodEnd = result && typeof result === "object" ? result.current_period_end : undefined;
          this.renewalDate =
            typeof periodEnd === "string" && moment(periodEnd, moment.ISO_8601, true).isValid()
              ? moment(periodEnd)
              : null;
        },
        error: () => {
          this.renewalDate = null;
        },
      });
  }
}
